import { Download, Loader2 } from 'lucide-react';
import React, { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';
import type { ExportFormat, OrgExportColumnsMode } from '@/types/export.types';

interface ExportCasesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Format picked in the download menu; drives the title + CTA copy. */
  format: ExportFormat;
  /** One-line description of the filters the export will apply (no counts). */
  filtersSummary: string;
  isExporting: boolean;
  /** Runs the export; the container closes the dialog on success. */
  onExport: (mode: OrgExportColumnsMode) => Promise<void> | void;
}

/**
 * Column-mode picker for the tier-2 org-wide export.
 *
 * Two ways to export the currently-filtered cases:
 * - Common columns: the shared create-case (intake) columns — every case in one file.
 * - All columns: one file per manufacturer, each with that manufacturer's full
 *   schema columns (mirrors the per-program case-list export).
 */
export const ExportCasesDialog: React.FC<ExportCasesDialogProps> = ({
  open,
  onOpenChange,
  format,
  filtersSummary,
  isExporting,
  onExport,
}) => {
  const [mode, setMode] = useState<OrgExportColumnsMode>('common');
  const formatLabel = format === 'csv' ? 'CSV' : 'Excel';

  // Reset the choice on close so a reopen starts fresh; ignore close requests
  // mid-export (the controls are disabled and files are still downloading).
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && isExporting) return;
    if (!nextOpen) setMode('common');
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle>Export as {formatLabel}</DialogTitle>
          <DialogDescription>Choose how much detail to include.</DialogDescription>
        </DialogHeader>

        <div className="border-t px-6 py-4 space-y-4">
          <div className="rounded-md border bg-muted/50 px-3 py-2">
            <p className="text-sm font-medium">Cases in this export</p>
            <p className="text-xs text-muted-foreground">{filtersSummary}</p>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium">Which columns?</p>
            <RadioGroup
              value={mode}
              onValueChange={value => setMode(value as OrgExportColumnsMode)}
              disabled={isExporting}
              className="gap-2"
            >
              <label
                htmlFor="org-export-common"
                className={cn(
                  'flex cursor-pointer items-start gap-3 rounded-md border p-3',
                  mode === 'common' && 'border-primary',
                  isExporting && 'cursor-not-allowed opacity-70',
                )}
              >
                <RadioGroupItem id="org-export-common" value="common" className="mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium leading-none">Common columns</p>
                  <p className="text-xs text-muted-foreground">
                    All columns shared by every manufacturer.
                  </p>
                </div>
              </label>

              <label
                htmlFor="org-export-all"
                className={cn(
                  'flex cursor-pointer items-start gap-3 rounded-md border p-3',
                  mode === 'all' && 'border-primary',
                  isExporting && 'cursor-not-allowed opacity-70',
                )}
              >
                <RadioGroupItem id="org-export-all" value="all" className="mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium leading-none">
                    All columns{' '}
                    <span className="font-normal text-muted-foreground">
                      · one file per manufacturer
                    </span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Each manufacturer&apos;s cases download as a separate file with all of its
                    columns.
                  </p>
                </div>
              </label>
            </RadioGroup>
          </div>
        </div>

        <DialogFooter className="border-t px-6 py-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isExporting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="gap-2"
            onClick={() => void onExport(mode)}
            disabled={isExporting}
            aria-busy={isExporting}
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Export {formatLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
